/* Recherche universelle + Assistant Fonbex flottant */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, Sparkles, X, Send, Loader2, ChevronRight, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/* ============ Universal search bar (top bar) ============ */
export function GlobalSearch() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const timer = useRef(null);
  const wrap = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q || q.length < 2) { setItems([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setItems(data.items || []);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q]);

  useEffect(() => {
    const handler = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goTo = (it) => {
    nav(it.route);
    setOpen(false); setQ("");
  };

  const KIND_LABEL = {
    company: "Société", building: "Immeuble", lot: "Lot",
    tenant: "Locataire", lease: "Bail", credit: "Crédit",
    charge: "Facture", document: "Document", deadline: "Échéance", mdb: "Projet MDB",
  };

  return (
    <div ref={wrap} className="relative flex-1 max-w-lg mx-2 hidden md:block">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher : SCI, immeuble, locataire, facture…"
          onFocus={() => q.length >= 2 && setOpen(true)}
          className="w-full pl-9 pr-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="global-search-input"
        />
      </div>
      {open && (
        <div className="absolute mt-1 w-full bg-background border rounded-xl shadow-xl overflow-hidden z-50 max-h-96 overflow-y-auto"
              data-testid="global-search-results">
          {loading && (
            <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />Recherche…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Aucun résultat.</div>
          )}
          {items.map((it, i) => (
            <button
              key={`${it.kind}-${it.id}-${i}`}
              onClick={() => goTo(it)}
              data-testid={`search-item-${it.kind}-${i}`}
              className="w-full flex justify-between items-center px-4 py-2 text-left hover:bg-secondary/60 border-b last:border-b-0"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{it.label}</div>
                <div className="text-xs text-muted-foreground truncate">{it.sub}</div>
              </div>
              <div className="flex items-center gap-2 text-xs text-primary">
                <span className="rounded-full bg-primary/10 px-2 py-0.5">{KIND_LABEL[it.kind] || it.kind}</span>
                <ChevronRight className="h-3 w-3" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ Floating Assistant ============ */
const FAB_MINIMIZED_KEY = "fonbex_fab_minimized";

export function FloatingAssistant() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);
  const [examples, setExamples] = useState([]);
  const [minimized, setMinimized] = useState(
    () => localStorage.getItem(FAB_MINIMIZED_KEY) === "1"
  );
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const nav = useNavigate();

  // Détection du clavier virtuel mobile via visualViewport — masque le FAB tant
  // que le clavier est ouvert (évite qu'il recouvre les inputs).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const check = () => setKeyboardOpen(vv.height < window.innerHeight * 0.75);
    vv.addEventListener("resize", check);
    check();
    return () => vv.removeEventListener("resize", check);
  }, []);

  const toggleMinimize = () => {
    setMinimized((m) => {
      const next = !m;
      localStorage.setItem(FAB_MINIMIZED_KEY, next ? "1" : "0");
      return next;
    });
  };

  useEffect(() => {
    api.get("/assistant/examples")
      .then(({ data }) => setExamples(data.items || []))
      .catch(() => setExamples([]));
  }, []);

  const send = async (text) => {
    const t = text || msg;
    if (!t.trim()) return;
    setLoading(true);
    const now = Date.now();
    setHistory((h) => [...h, { role: "user", text: t, ts: now }]);
    setMsg("");
    try {
      const { data } = await api.post("/assistant/chat", { message: t });
      if (data.kind === "navigate" && data.requires_confirmation) {
        setPending(data);
      } else if (data.kind === "navigate") {
        // Direct nav (pas de création → simple ouverture)
        setHistory((h) => [...h, { role: "assistant", text: data.message, action: data, ts: Date.now() }]);
        setTimeout(() => { nav(data.route); setOpen(false); }, 500);
      } else if (data.kind === "list") {
        setHistory((h) => [...h, {
          role: "assistant", text: data.message,
          list: data.items || [], ts: Date.now(),
        }]);
      } else {
        setHistory((h) => [...h, { role: "assistant", text: data.message, ts: Date.now() }]);
      }
    } catch (e) {
      setHistory((h) => [...h, { role: "assistant", text: "Erreur assistant : " + (e.response?.data?.detail || String(e)), ts: Date.now() }]);
    } finally { setLoading(false); }
  };

  const confirmPending = () => {
    if (!pending) return;
    setHistory((h) => [...h, { role: "assistant", text: pending.message + " ✓", ts: Date.now() }]);
    nav(pending.route);
    setPending(null);
    setOpen(false);
  };

  return (
    <>
      {/* Floating button — se masque quand le clavier virtuel est ouvert ou quand
          l'utilisateur l'a réduit. Utilise la safe-area iOS et un décalage suffisant
          pour ne pas recouvrir les boutons d'action fixes (bas de formulaires). */}
      {!keyboardOpen && (
        <div
          className="fixed z-30 flex items-center gap-2"
          style={{
            right: "max(1rem, env(safe-area-inset-right, 0px) + 0.5rem)",
            bottom: "max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))",
          }}
        >
          {minimized ? (
            <button
              onClick={toggleMinimize}
              className="rounded-full bg-primary/90 text-primary-foreground p-2.5 shadow-lg hover:scale-105 transition"
              data-testid="assistant-fab-restore"
              aria-label="Afficher l'assistant Fonbex"
              title="Afficher l'assistant Fonbex"
            >
              <Sparkles className="h-4 w-4" />
            </button>
          ) : (
            <div className="flex items-center rounded-full bg-primary text-primary-foreground shadow-lg overflow-hidden">
              <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-2 pl-4 pr-3 py-2.5 sm:py-3 hover:bg-primary/90 transition"
                data-testid="assistant-fab"
                aria-label="Assistant Fonbex"
              >
                <Sparkles className="h-5 w-5" />
                <span className="hidden sm:inline font-medium">Assistant</span>
              </button>
              <button
                onClick={toggleMinimize}
                className="border-l border-primary-foreground/20 px-2 py-2.5 sm:py-3 hover:bg-primary/90 transition"
                data-testid="assistant-fab-minimize"
                aria-label="Réduire l'assistant"
                title="Réduire — le bouton restera accessible en petit"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-6"
              onClick={(e) => e.target === e.currentTarget && setOpen(false)}
              data-testid="assistant-panel">
          <div className="w-full sm:max-w-lg bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <div className="flex items-center gap-2 font-heading">
                <Sparkles className="h-4 w-4 text-primary" />
                Assistant Fonbex
              </div>
              <button onClick={() => setOpen(false)} data-testid="assistant-close">
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
              {history.length === 0 && (
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Bonjour ! Que souhaitez-vous faire ?
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {examples.slice(0, 6).map((e, i) => (
                      <button key={i} onClick={() => send(e)}
                              data-testid={`assistant-example-${i}`}
                              className="text-xs px-3 py-1.5 rounded-full bg-secondary hover:bg-secondary/80 transition">
                        {e}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {history.map((m, i) => (
                <div key={m.ts || `msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"
                  }`}>
                    <div>{m.text}</div>
                    {m.list && m.list.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {m.list.slice(0, 8).map((li, j) => (
                          <div key={`${li.label || "item"}-${j}`} className="text-xs bg-background/60 rounded px-2 py-1">
                            <div className="font-medium">{li.label}</div>
                            <div className="text-muted-foreground">{li.sub}</div>
                          </div>
                        ))}
                        {m.list.length === 0 && <div className="text-xs text-muted-foreground italic">Aucun élément.</div>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {pending && (
                <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-3">
                  <div className="text-sm font-medium mb-2">Confirmez l{"'"}action</div>
                  <div className="text-xs text-muted-foreground mb-3">{pending.message}</div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={confirmPending} data-testid="assistant-confirm">
                      Valider
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPending(null)} data-testid="assistant-cancel">
                      Annuler
                    </Button>
                  </div>
                </div>
              )}
              {loading && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />Réflexion…
                </div>
              )}
            </div>

            <div className="border-t p-3 flex gap-2">
              <Input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Que voulez-vous faire ?"
                data-testid="assistant-input"
              />
              <Button onClick={() => send()} disabled={!msg.trim() || loading} data-testid="assistant-send">
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
